// Isomorphic analytics facade
// - In server contexts, re-export the real implementation
// - In client contexts, send lightweight POSTs to analytics ingest endpoints

const isServer = typeof window === 'undefined'

// Event types shared across client/server
export const EventTypes = {
  SIGNUP: 'signup',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  TRIAL_STARTED: 'trial_started',
  TRIAL_CONVERTED: 'trial_converted',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  PLAN_GENERATED: 'plan_generated',
  PLAN_REGENERATED: 'plan_regenerated',
  RECIPE_SWAPPED: 'recipe_swapped',
  PAYMENT_FAILED: 'payment_failed',
} as const

type AnyObj = Record<string, any>

function normalizeArgs(a: any, b?: any, c?: any): { userId: string; type: string; meta?: AnyObj } {
  // Support both (userId, type, meta) and (type, userId, meta)
  if (typeof a === 'string' && typeof b === 'string') {
    // Heuristic: if the first looks like a UID, treat as (userId, type)
    const looksLikeUid = /[a-z0-9]{6,}/i.test(a) && !a.includes(' ')
    if (looksLikeUid) return { userId: a, type: b, meta: c }
    return { userId: b, type: a, meta: c }
  }
  // Fallback
  return { userId: String(a ?? ''), type: String(b ?? ''), meta: c }
}

export async function emitEvent(a: any, b?: any, c?: any) {
  if (isServer) {
    // Use dynamic string to prevent Vite from bundling server code in client build
    const serverPath = '../server' + '/analytics/eventTracking'
    const mod = await import(/* @vite-ignore */ serverPath)
    const { userId, type, meta } = normalizeArgs(a, b, c)
    return mod.emitEvent(userId, type, meta)
  }
  try {
    const { userId, type, meta } = normalizeArgs(a, b, c)
    await fetch('/api/analytics/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, type, ...meta }),
    })
  } catch {
    // Swallow analytics errors on client
  }
}

// Simple client-side tracker used across the app
export async function track(event: string, props?: AnyObj) {
  // Use a fixed channel for anonymous client-side calls
  // Pass event name first so normalizeArgs maps correctly: type=event, userId='web'
  return emitEvent(event, 'web', props)
}

// No-op shims to satisfy imports during migration
export async function initAnalytics() {
  // Place real analytics init here if/when needed
  return
}

export function setContext(_ctx: AnyObj) {
  // Store context if you later need to prefix events; currently a no-op
}

export async function recordAiUsage(userId: string, tokens: number, costUsd: number, success: boolean, meta?: AnyObj) {
  if (isServer) {
    // Use dynamic string to prevent Vite from bundling server code in client build
    const serverPath = '../server' + '/analytics/eventTracking'
    const mod = await import(/* @vite-ignore */ serverPath)
    return mod.recordAiUsage(userId, tokens, costUsd, success, meta)
  }
  try {
    await fetch('/api/analytics/interaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, type: 'ai_usage', tokens, costUsd, success, ...meta }),
    })
  } catch {
    // Swallow analytics errors on client
  }
}
